import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateProfile, updateField } from '../../store/slices/profileSlice';
import { updateUser } from '../../store/slices/authSlice';
import jwtService from '../../auth/jwt/jwtService';

async function getCroppedImg(imageSrc: string, pixelCrop: { x: number; y: number; width: number; height: number }): Promise<File> {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.addEventListener('load', () => resolve(img));
        img.addEventListener('error', reject);
        img.src = imageSrc;
    });
    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
    return new Promise<File>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) { reject(new Error('Canvas is empty')); return; }
            resolve(new File([blob], 'profile.jpg', { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.95);
    });
}

const profileDraftMemory: Record<string, { draft: any; snapshot: string }> = {};

const Profile: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const profileData = useAppSelector((state) => state.profile);
    const authUser = useAppSelector((state) => state.auth.user);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Crop modal state
    const [cropModalSrc, setCropModalSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
    const onCropComplete = useCallback((_: any, pixels: any) => { setCroppedAreaPixels(pixels); }, []);
    const [tempProfileData, setTempProfileData] = useState({ title: '', age: '', weight: '', height: '' });
    const [lastSavedSnapshot, setLastSavedSnapshot] = useState<string>('');
    const initials = `${(profileData.name || 'U').charAt(0)}${(profileData.surname || '').charAt(0)}`.toUpperCase();

    const getProfileSnapshot = (data: typeof profileData) =>
        JSON.stringify({
            name: data.name || '',
            surname: data.surname || '',
            title: data.title || '',
            age: data.age || 0,
            weight: data.weight || 0,
            height: data.height || 0,
            email: data.email || '',
            gender: data.gender || '',
            dateOfBirth: data.dateOfBirth || '',
            address: data.address || '',
            city: data.city || '',
            province: data.province || '',
            phone: data.phone || '',
            fcMax: data.fcMax || 0,
            fcRiposo: data.fcRiposo || 0,
            fcSoglia: data.fcSoglia || 0,
            restDay: data.restDay || '',
            notes: data.notes || '',
        });

    const hasUnsavedChanges = Boolean(lastSavedSnapshot) && getProfileSnapshot(profileData) !== lastSavedSnapshot;

    const applyApiUserToStore = (user: any) => {
        const firstName = (user.firstName || '').trim();
        const lastName = (user.lastName || '').trim();

        const mappedProfile = {
            name: firstName,
            surname: lastName,
            email: user.email || '',
            gender: user.gender || '',
            dateOfBirth: user.dateOfBirth ? String(user.dateOfBirth).split('T')[0] : '',
            title: user.title || '',
            age: user.age ?? 0,
            weight: user.weight ?? 0,
            height: user.height ?? 0,
            address: user.address || '',
            city: user.city || '',
            province: user.province || '',
            phone: user.phoneNumber || '',
            fcMax: user.fcMax ?? 0,
            fcRiposo: user.fcRiposo ?? 0,
            fcSoglia: user.fcSoglia ?? 0,
            restDay: user.restDay || '',
            notes: user.notes || '',
        };

        dispatch(updateUser({
            firstName,
            lastName,
            name: `${firstName} ${lastName}`.trim(),
            email: user.email,
        }));

        dispatch(updateProfile(mappedProfile));
        const snapshot = getProfileSnapshot(mappedProfile);
        setLastSavedSnapshot(snapshot);
    };

    const loadProfile = async () => {
        if (!authUser) return;

        try {
            const me = await jwtService.getCurrentUser();
            applyApiUserToStore(me);
        } catch (error) {
            const firstNameFromAuth = authUser.firstName || '';
            const lastNameFromAuth = authUser.lastName || '';
            const [firstNameFromName = '', ...rest] = (authUser.name || '').trim().split(' ');
            const lastNameFromName = rest.join(' ');

            dispatch(updateProfile({
                name: firstNameFromAuth || firstNameFromName,
                surname: lastNameFromAuth || lastNameFromName,
                email: authUser.email || '',
            }));
            setLastSavedSnapshot(
                getProfileSnapshot({
                    ...profileData,
                    name: firstNameFromAuth || firstNameFromName,
                    surname: lastNameFromAuth || lastNameFromName,
                    email: authUser.email || '',
                }),
            );
        }
    };

    useEffect(() => {
        if (!authUser?.id) return;

        const hydrateProfile = async () => {
            const inMemoryDraft = profileDraftMemory[authUser.id];

            if (inMemoryDraft?.snapshot) {
                setLastSavedSnapshot(inMemoryDraft.snapshot);
            }

            if (inMemoryDraft?.draft) {
                dispatch(updateProfile(inMemoryDraft.draft));
                // Keep unsaved draft when switching sidebar tabs in same runtime.
                if (
                    inMemoryDraft.snapshot &&
                    getProfileSnapshot(inMemoryDraft.draft) !== inMemoryDraft.snapshot
                ) {
                    return;
                }
            }

            await loadProfile();
        };

        hydrateProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser?.id]);

    useEffect(() => {
        if (!authUser?.id || !lastSavedSnapshot) return;
        profileDraftMemory[authUser.id] = {
            draft: profileData,
            snapshot: lastSavedSnapshot,
        };
    }, [authUser?.id, profileData, lastSavedSnapshot]);

    const saveProfile = async (data: typeof profileData = profileData) => {
        setIsSaving(true);
        setStatusMessage(null);

        try {
            const updated = await jwtService.updateProfile({
                firstName: data.name.trim(),
                lastName: data.surname.trim(),
                email: data.email.trim(),
                gender: data.gender,
                dateOfBirth: data.dateOfBirth || undefined,
                title: data.title,
                age: data.age,
                weight: data.weight,
                height: data.height,
                address: data.address,
                city: data.city,
                province: data.province,
                phoneNumber: data.phone,
                fcMax: data.fcMax,
                fcRiposo: data.fcRiposo,
                fcSoglia: data.fcSoglia,
                restDay: data.restDay,
                notes: data.notes,
            });

            applyApiUserToStore(updated);
            setStatusMessage({ type: 'success', text: t('profile.profileSavedSuccess') });
        } catch (error: any) {
            setStatusMessage({
                type: 'error',
                text: error?.message || t('profile.photoError'),
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        await saveProfile(profileData);
    };

    const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
            setCropModalSrc(reader.result as string);
        };
        reader.readAsDataURL(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCropConfirm = async () => {
        if (!cropModalSrc || !croppedAreaPixels) return;
        setIsUploadingPhoto(true);
        setCropModalSrc(null);
        setStatusMessage(null);
        try {
            const croppedFile = await getCroppedImg(cropModalSrc, croppedAreaPixels);
            const updated = await jwtService.uploadProfileImage(croppedFile);
            dispatch(updateUser({ avatar: updated.profileImage ?? undefined }));
            setStatusMessage({ type: 'success', text: t('profile.photoPicUpdated') });
        } catch (error: any) {
            setStatusMessage({ type: 'error', text: error?.message || t('profile.photoError') });
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const resetToLatestSaved = async () => {
        await loadProfile();
        setStatusMessage({ type: 'success', text: t('profile.profileResetSuccess') });
    };

    const handleReset = async () => {
        if (hasUnsavedChanges) {
            setIsDiscardModalOpen(true);
            return;
        }

        await resetToLatestSaved();
    };

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!hasUnsavedChanges) return;
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);
    return (
        <div className="flex flex-col min-h-screen">
            {/* Header */}
            <PageHeader
                icon="fa-solid fa-user-gear"
                title={t('profile.title')}
                subtitle={t('profile.subtitle')}
                primaryButton={{
                    label: isSaving ? t('profile.saving') : t('profile.saveProfile'),
                    icon: 'fa-solid fa-floppy-disk',
                    onClick: handleSave,
                }}
                secondaryButton={{
                    label: t('profile.resetChanges'),
                    icon: 'fa-solid fa-rotate-left',
                    onClick: handleReset,
                }}
            />

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-8">
                <div className="max-w-6xl mx-auto flex flex-col gap-6 pb-20">
                    {statusMessage && (
                        <div
                            className={`rounded-lg px-4 py-3 border text-sm font-medium ${statusMessage.type === 'success'
                                ? 'bg-cyan-50 border-cyan-200 text-cyan-700'
                                : 'bg-blue-50 border-blue-300 text-blue-700'
                                }`}
                        >
                            {statusMessage.text}
                        </div>
                    )}

                    {/* Profile Overview Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden relative">
                        {/* Edit Button */}
                        <button
                            onClick={() => {
                                setTempProfileData({
                                    title: profileData.title,
                                    age: profileData.age ? String(profileData.age) : '',
                                    weight: profileData.weight ? String(profileData.weight) : '',
                                    height: profileData.height ? String(profileData.height) : '',
                                });
                                setIsEditModalOpen(true);
                            }}
                            className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-all shadow-lg hover:shadow-xl"
                        >
                            <i className="fa-solid fa-pen text-sm"></i>
                        </button>

                        {/* Background Banner */}
                        <div className="h-32 bg-gradient-to-r from-slate-800 to-slate-900 relative">
                            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
                            <div className="absolute right-0 bottom-0 p-4 opacity-10">
                                <i className="fa-solid fa-person-biking text-8xl text-white"></i>
                            </div>
                        </div>

                        <div className="px-8 pb-8 flex flex-col md:flex-row gap-6 -mt-16 relative z-10">
                            {/* Avatar Upload */}
                            <div className="relative group flex-shrink-0">
                                <div className="w-32 h-32 rounded-2xl bg-white p-1 shadow-lg">
                                    {authUser?.avatar ? (
                                        <img
                                            src={authUser.avatar.startsWith('http') ? authUser.avatar : `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '')}${authUser.avatar}`}
                                            alt="Profile"
                                            className="w-full h-full rounded-xl object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center text-white text-4xl font-bold">
                                            {initials}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploadingPhoto}
                                    className="absolute bottom-2 right-2 w-8 h-8 bg-primary text-white rounded-lg shadow-md hover:bg-cyan-600 transition-colors flex items-center justify-center cursor-pointer disabled:opacity-60"
                                >
                                    <i className={isUploadingPhoto ? 'fa-solid fa-spinner fa-spin text-xs' : 'fa-solid fa-camera text-xs'}></i>
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handlePhotoUpload}
                                />
                                {/* Crop Modal */}
                                {cropModalSrc && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
                                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden max-h-[90dvh]">
                                            {/* Header */}
                                            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-6 py-4 flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-white">
                                                    <i className="fa-solid fa-crop-simple"></i>
                                                    <span className="font-semibold text-sm">Adjust Profile Picture</span>
                                                </div>
                                                <button onClick={() => setCropModalSrc(null)} className="text-white/80 hover:text-white transition-colors">
                                                    <i className="fa-solid fa-xmark text-lg"></i>
                                                </button>
                                            </div>

                                            {/* Crop Area */}
                                            <div className="relative w-full bg-slate-900" style={{ height: '320px' }}>
                                                <Cropper
                                                    image={cropModalSrc}
                                                    crop={crop}
                                                    zoom={zoom}
                                                    aspect={1}
                                                    cropShape="round"
                                                    showGrid={false}
                                                    onCropChange={setCrop}
                                                    onZoomChange={setZoom}
                                                    onCropComplete={onCropComplete}
                                                    style={{ containerStyle: { borderRadius: 0 } }}
                                                />
                                            </div>

                                            {/* Zoom Slider */}
                                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                                                <div className="flex items-center gap-3">
                                                    <i className="fa-solid fa-magnifying-glass-minus text-slate-400 text-sm"></i>
                                                    <input
                                                        type="range"
                                                        min={1}
                                                        max={3}
                                                        step={0.05}
                                                        value={zoom}
                                                        onChange={(e) => setZoom(Number(e.target.value))}
                                                        className="flex-1 h-2 appearance-none rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 cursor-pointer accent-cyan-500"
                                                    />
                                                    <i className="fa-solid fa-magnifying-glass-plus text-slate-400 text-sm"></i>
                                                    <span className="text-xs text-slate-500 w-10 text-right">{Math.round(zoom * 100)}%</span>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="px-6 py-4 flex gap-3 justify-end border-t border-slate-100">
                                                <button
                                                    onClick={() => setCropModalSrc(null)}
                                                    className="px-4 py-2 rounded-lg border border-cyan-200 text-cyan-700 bg-cyan-50 text-sm font-medium hover:bg-cyan-100 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleCropConfirm}
                                                    className="px-5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all shadow"
                                                >
                                                    <i className="fa-solid fa-check mr-2"></i>Apply
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 flex flex-col justify-end md:pb-0 pt-10">
                                <h2 className="text-2xl font-bold text-slate-800">{profileData.name} {profileData.surname}</h2>
                            </div>

                            <div className="flex gap-3 flex-wrap md:self-start md:pt-10">
                                <div className="px-4 py-2 rounded-lg bg-cyan-50 text-cyan-700 text-sm font-bold border border-cyan-100 flex flex-col items-center">
                                    <span className="text-xs text-cyan-400 uppercase">Age</span>
                                    <span>{profileData.age || '--'}</span>
                                </div>
                                <div className="px-4 py-2 rounded-lg bg-blue-50 text-blue-600 text-sm font-bold border border-blue-100 flex flex-col items-center">
                                    <span className="text-xs text-blue-400 uppercase">Weight</span>
                                    <span>{profileData.weight ? `${profileData.weight} kg` : '--'}</span>
                                </div>
                                <div className="px-4 py-2 rounded-lg bg-cyan-50 text-cyan-600 text-sm font-bold border border-cyan-100 flex flex-col items-center">
                                    <span className="text-xs text-cyan-400 uppercase">Height</span>
                                    <span>{profileData.height ? `${profileData.height} cm` : '--'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Grid */}
                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

                        {/* Left Column */}
                        <div className="xl:col-span-7 flex flex-col gap-6">

                            {/* Personal Info Card */}
                            <SectionCard
                                title={t('profile.sectionPersonalTitle')}
                                icon="fa-solid fa-id-card"
                                variant="cyan"
                                badge={t('profile.badgePersonal')}
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                    {/* Name */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.fieldNome')} <span className="text-cyan-500">*</span></label>
                                        <div className="relative">
                                            <i className="fa-solid fa-user absolute left-3 top-3.5 text-slate-400 text-sm"></i>
                                            <input type="text" value={profileData.name} onChange={(e) => dispatch(updateField({ field: 'name', value: e.target.value }))} placeholder={t('profile.placeholderFirstName')} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white focus:shadow-lg focus:shadow-primary/10" />
                                        </div>
                                    </div>

                                    {/* Surname */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.fieldCognome')} <span className="text-cyan-500">*</span></label>
                                        <div className="relative">
                                            <i className="fa-regular fa-user absolute left-3 top-3.5 text-slate-400 text-sm"></i>
                                            <input type="text" value={profileData.surname} onChange={(e) => dispatch(updateField({ field: 'surname', value: e.target.value }))} placeholder={t('profile.placeholderLastName')} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white focus:shadow-lg focus:shadow-primary/10" />
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div className="space-y-1.5 md:col-span-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.fieldEmail')} <span className="text-cyan-500">*</span></label>
                                        <div className="relative">
                                            <i className="fa-solid fa-envelope absolute left-3 top-3.5 text-slate-400 text-sm"></i>
                                            <input type="email" value={profileData.email} onChange={(e) => dispatch(updateField({ field: 'email', value: e.target.value }))} placeholder={t('profile.placeholderEmail')} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white focus:shadow-lg focus:shadow-primary/10" />
                                        </div>
                                    </div>

                                    {/* Gender */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.fieldSesso')} <span className="text-cyan-500">*</span></label>
                                        <div className="relative">
                                            <i className="fa-solid fa-venus-mars absolute left-3 top-3.5 text-slate-400 text-sm"></i>
                                            <select value={profileData.gender} onChange={(e) => dispatch(updateField({ field: 'gender', value: e.target.value }))} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white appearance-none cursor-pointer">
                                                <option value="">{t('profile.optionSelect')}</option>
                                                <option value="M">{t('profile.optionMale')}</option>
                                                <option value="F">{t('profile.optionFemale')}</option>
                                            </select>
                                            <i className="fa-solid fa-chevron-down absolute right-3 top-3.5 text-slate-400 text-xs pointer-events-none"></i>
                                        </div>
                                    </div>

                                    {/* DOB */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.fieldDataNascita')}</label>
                                        <div className="relative">
                                            <i className="fa-solid fa-cake-candles absolute left-3 top-3.5 text-slate-400 text-sm"></i>
                                            <input type="date" value={profileData.dateOfBirth} onChange={(e) => dispatch(updateField({ field: 'dateOfBirth', value: e.target.value }))} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white" />
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Contact Info Card */}
                            <SectionCard
                                title={t('profile.sectionContactTitle')}
                                icon="fa-solid fa-address-book"
                                variant="cyan"
                                badge={t('profile.badgeContact')}
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                    {/* Address */}
                                    <div className="space-y-1.5 md:col-span-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.fieldIndirizzo')}</label>
                                        <div className="relative">
                                            <i className="fa-solid fa-map-location-dot absolute left-3 top-3.5 text-slate-400 text-sm"></i>
                                            <input type="text" value={profileData.address} onChange={(e) => dispatch(updateField({ field: 'address', value: e.target.value }))} placeholder={t('profile.placeholderAddress')} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white" />
                                        </div>
                                    </div>

                                    {/* City */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.fieldCitta')}</label>
                                        <div className="relative">
                                            <i className="fa-solid fa-city absolute left-3 top-3.5 text-slate-400 text-sm"></i>
                                            <input type="text" value={profileData.city} onChange={(e) => dispatch(updateField({ field: 'city', value: e.target.value }))} placeholder={t('profile.placeholderCity')} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white" />
                                        </div>
                                    </div>

                                    {/* Province */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.fieldProvincia')}</label>
                                        <div className="relative">
                                            <i className="fa-solid fa-map absolute left-3 top-3.5 text-slate-400 text-sm"></i>
                                            <input type="text" value={profileData.province} onChange={(e) => dispatch(updateField({ field: 'province', value: e.target.value }))} placeholder={t('profile.placeholderProvince')} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white" />
                                        </div>
                                    </div>

                                    {/* Phone */}
                                    <div className="space-y-1.5 md:col-span-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.fieldTelefoni')}</label>
                                        <div className="relative">
                                            <i className="fa-solid fa-phone absolute left-3 top-3.5 text-slate-400 text-sm"></i>
                                            <textarea rows={2} value={profileData.phone} onChange={(e) => dispatch(updateField({ field: 'phone', value: e.target.value }))} placeholder={t('profile.placeholderPhone')} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white resize-none"></textarea>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>
                        </div>

                        {/* Right Column */}
                        <div className="xl:col-span-5 flex flex-col gap-6">

                            {/* Heart Rate Stats */}
                            <SectionCard
                                title={t('profile.sectionHRTitle')}
                                icon="fa-solid fa-heart-pulse"
                                variant="cyan"
                                badge={t('profile.badgeHealth')}
                            >
                                <div className="space-y-5">
                                    {/* FC Max */}
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1 space-y-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.labelFCMax')}</label>
                                            <div className="relative">
                                                <input type="number" value={profileData.fcMax} onChange={(e) => dispatch(updateField({ field: 'fcMax', value: parseInt(e.target.value) || 0 }))} className="w-full pl-4 pr-12 py-3 bg-cyan-50 border border-cyan-100 rounded-lg text-lg font-bold text-cyan-700 focus:outline-none focus:bg-white focus:border-cyan-300" />
                                                <span className="absolute right-3 top-4 text-xs font-bold text-cyan-400">BPM</span>
                                            </div>
                                        </div>
                                        <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-500">
                                            <i className="fa-solid fa-fire-flame-curved text-xl"></i>
                                        </div>
                                    </div>

                                    {/* FC Riposo */}
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1 space-y-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.labelFCRiposo')}</label>
                                            <div className="relative">
                                                <input type="number" value={profileData.fcRiposo} onChange={(e) => dispatch(updateField({ field: 'fcRiposo', value: parseInt(e.target.value) || 0 }))} className="w-full pl-4 pr-12 py-3 bg-cyan-50 border border-cyan-100 rounded-lg text-lg font-bold text-cyan-700 focus:outline-none focus:bg-white focus:border-cyan-300" />
                                                <span className="absolute right-3 top-4 text-xs font-bold text-cyan-400">BPM</span>
                                            </div>
                                        </div>
                                        <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-500">
                                            <i className="fa-solid fa-bed text-xl"></i>
                                        </div>
                                    </div>

                                    {/* FC Soglia */}
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1 space-y-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.labelFCSoglia')}</label>
                                            <div className="relative">
                                                <input type="number" value={profileData.fcSoglia} onChange={(e) => dispatch(updateField({ field: 'fcSoglia', value: parseInt(e.target.value) || 0 }))} className="w-full pl-4 pr-12 py-3 bg-cyan-50 border border-cyan-100 rounded-lg text-lg font-bold text-cyan-700 focus:outline-none focus:bg-white focus:border-cyan-300" />
                                                <span className="absolute right-3 top-4 text-xs font-bold text-cyan-400">BPM</span>
                                            </div>
                                        </div>
                                        <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-500">
                                            <i className="fa-solid fa-gauge-high text-xl"></i>
                                        </div>
                                    </div>

                                    {/* HR Zones */}
                                    <div className="pt-4 mt-2 border-t border-slate-100">
                                        <p className="text-xs font-bold text-slate-400 uppercase mb-2">{t('profile.estimatedZones')}</p>
                                        <div className="h-3 w-full rounded-full bg-slate-100 flex overflow-hidden">
                                            <div className="w-[50%] bg-cyan-200"></div>
                                            <div className="w-[20%] bg-cyan-400"></div>
                                            <div className="w-[15%] bg-blue-400"></div>
                                            <div className="w-[10%] bg-blue-500"></div>
                                            <div className="w-[5%] bg-blue-700"></div>
                                        </div>
                                        <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-medium">
                                            <span>Z1</span>
                                            <span>Z2</span>
                                            <span>Z3</span>
                                            <span>Z4</span>
                                            <span>Z5</span>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Notes & Preferences */}
                            <SectionCard
                                title={t('profile.sectionNotesTitle')}
                                icon="fa-solid fa-clipboard-list"
                                variant="cyan"
                                badge={t('profile.badgeSettings')}
                                className="flex-1"
                            >
                                <div className="space-y-6">
                                    {/* Rest Days */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.labelRestDays')}</label>
                                        <div className="flex flex-wrap gap-2">
                                            {(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const).map((day) => (
                                                <button
                                                    key={day}
                                                    type="button"
                                                    onClick={() => dispatch(updateField({ field: 'restDay', value: profileData.restDay === day ? '' : day }))}
                                                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${profileData.restDay === day
                                                        ? 'bg-primary text-white border-primary hover:bg-cyan-600'
                                                        : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    {t(`profile.restDayLabels.${day}`)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('profile.labelNotes')}</label>
                                        <div className="relative">
                                            <textarea rows={4} value={profileData.notes} onChange={(e) => dispatch(updateField({ field: 'notes', value: e.target.value }))} placeholder={t('profile.placeholderNotes')} className="w-full p-4 bg-cyan-50/50 border border-cyan-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white resize-none"></textarea>
                                            <i className="fa-solid fa-pen absolute right-3 bottom-3 text-cyan-400/50 text-sm"></i>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>
                        </div>

                    </div>
                </div>
            </div>

            {/* Edit Profile Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90dvh]">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                    <i className="fa-solid fa-user-pen text-white text-lg"></i>
                                </div>
                                <h3 className="text-xl font-bold text-white">{t('profile.editModalTitle')}</h3>
                            </div>
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="w-8 h-8 rounded-lg hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-4 md:p-6 space-y-3 md:space-y-4 bg-slate-50 overflow-y-auto">
                            {/* Stats Grid */}
                            <div className="grid grid-cols-3 gap-2 sm:gap-3 pt-2">
                                {/* Age */}
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-blue-600 uppercase tracking-wide">{t('profile.labelAge')}</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={tempProfileData.age}
                                        onChange={(e) =>
                                            setTempProfileData({
                                                ...tempProfileData,
                                                age: e.target.value.replace(/\D/g, ''),
                                            })
                                        }
                                        className="w-full px-3 py-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-600 font-bold text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Weight */}
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-blue-600 uppercase tracking-wide">{t('profile.labelWeight')}</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={tempProfileData.weight}
                                        onChange={(e) =>
                                            setTempProfileData({
                                                ...tempProfileData,
                                                weight: e.target.value.replace(/\D/g, ''),
                                            })
                                        }
                                        className="w-full px-3 py-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-600 font-bold text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Height */}
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-cyan-600 uppercase tracking-wide">{t('profile.labelHeight')}</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={tempProfileData.height}
                                        onChange={(e) =>
                                            setTempProfileData({
                                                ...tempProfileData,
                                                height: e.target.value.replace(/\D/g, ''),
                                            })
                                        }
                                        className="w-full px-3 py-3 bg-cyan-50 border border-cyan-200 rounded-lg text-cyan-600 font-bold text-center focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-white border-t border-slate-200 flex gap-3">
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    const mergedProfile = {
                                        ...profileData,
                                        title: tempProfileData.title,
                                        age: tempProfileData.age === '' ? 0 : parseInt(tempProfileData.age, 10) || 0,
                                        weight: tempProfileData.weight === '' ? 0 : parseInt(tempProfileData.weight, 10) || 0,
                                        height: tempProfileData.height === '' ? 0 : parseInt(tempProfileData.height, 10) || 0,
                                    };

                                    dispatch(updateProfile({
                                        title: mergedProfile.title,
                                        age: mergedProfile.age,
                                        weight: mergedProfile.weight,
                                        height: mergedProfile.height,
                                    }));
                                    setIsEditModalOpen(false);
                                    setStatusMessage({
                                        type: 'success',
                                        text: t('profile.draftUpdated'),
                                    });
                                }}
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-xl"
                            >
                                <i className="fa-solid fa-check mr-2"></i>
                                {t('profile.btnSaveChanges')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Discard Changes Modal */}
            {isDiscardModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90dvh]">
                        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                    <i className="fa-solid fa-triangle-exclamation text-white text-lg"></i>
                                </div>
                                <h3 className="text-lg font-bold text-white">{t('profile.discardModalTitle')}</h3>
                            </div>
                            <button
                                onClick={() => setIsDiscardModalOpen(false)}
                                className="w-8 h-8 rounded-lg hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>

                        <div className="p-4 md:p-6 bg-slate-50 overflow-y-auto">
                            <p className="text-slate-700 text-sm leading-relaxed">
                                {t('profile.discardModalBody')}
                            </p>
                        </div>

                        <div className="px-6 py-4 bg-white border-t border-slate-200 flex gap-3">
                            <button
                                onClick={() => setIsDiscardModalOpen(false)}
                                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors"
                            >
                                {t('profile.btnKeepEditing')}
                            </button>
                            <button
                                onClick={async () => {
                                    setIsDiscardModalOpen(false);
                                    await resetToLatestSaved();
                                }}
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-xl"
                            >
                                {t('profile.btnDiscard')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
